import { useCallback, useEffect, useMemo, useState } from 'react';

export type UseSelectableItemsIdType = string | number;

interface BaseType {
    id: UseSelectableItemsIdType;
    _disabled?: boolean;
}

export type SelectableItem<T extends BaseType> = T & {
    _selected: boolean;
    _disabled: boolean;
};

interface UseSelectableItemsResponse<T extends BaseType> {
    selectableItems: SelectableItem<T>[];
    setSelectedItem: (id: UseSelectableItemsIdType, checked: boolean) => void;
    disableItem: (id: UseSelectableItemsIdType, disabled: boolean) => void;
    hasSelectedItems: boolean;
    selectedIds: UseSelectableItemsIdType[];
    allItemsSelected: boolean;
    selectAllItems: (selected: boolean) => void;
}

export function useSelectableItems<T extends BaseType>(
    items: undefined | null | T[]
): UseSelectableItemsResponse<T> {
    const [selectedItems, setSelectedItems] = useState<SelectableItem<T>[]>([]);

    useEffect(() => {
        if (items) {
            setSelectedItems(items.map(x => {
                const currentItem = selectedItems.find(y => y.id === x.id);
                return {
                    ...x,
                    _selected: currentItem && !x._disabled ? currentItem._selected: false,
                    _disabled: !!x._disabled
                };
            }));
        }
    }, [items]);

    const handleSetSelectedItem = useCallback(
        (id, checked) => {
            const newItems = selectedItems.map(x => {
                if (x.id === id) {
                    return { ...x, _selected: checked };
                } else {
                    return x;
                }
            });

            setSelectedItems(newItems);
        },
        [selectedItems, setSelectedItems]
    );

    const handleDisableItem = (id: UseSelectableItemsIdType, disabled: boolean) => {
        const newItems = selectedItems.map(x => {
            if (x.id === id) {
                return { ...x, _disabled: disabled, _selected: false };
            } else {
                return x;
            }
        });

        setSelectedItems(newItems);
    };

    const [hasSelectedItems, selectedIds, allItemsSelected] = useMemo(() => {
        let selectedIds: UseSelectableItemsIdType[] = [];

        for (let i = 0; i < selectedItems.length; i++) {
            const item = selectedItems[i];
            if (item._selected) {
                selectedIds.push(item.id);
            } else if (!item._disabled) {
            }
        }

        const hasSelectedItems = selectedIds.length > 0;
        const allItemsSelected = selectedIds.length > 0 ? selectedIds.length === selectedItems.filter(x => !x._disabled).length : false;

        return [hasSelectedItems, selectedIds, allItemsSelected];
    }, [selectedItems]);

    const handleSelectAllItems = useCallback(
        (selected: boolean) => {
            const newItems = selectedItems.map(x => {
                return { ...x, _selected: x._disabled ? false : selected };
            });
            setSelectedItems(newItems);
        },
        [selectedItems, setSelectedItems]
    );

    return {
        selectableItems: selectedItems,
        setSelectedItem: handleSetSelectedItem,
        disableItem: handleDisableItem,
        hasSelectedItems,
        selectedIds,
        allItemsSelected,
        selectAllItems: handleSelectAllItems
    };
}
